/**
 * GameState の生成と、セーブ／ロード（localStorage）。
 */

import type { GameState } from '../types/scenario';

export const SAVE_KEY = 'netops-rookie:save:v1';
export const SETTINGS_KEY = 'netops-rookie:settings:v1';

export function createInitialState(playerName: string, firstChapterId: string): GameState {
  return {
    playerName,
    currentChapterId: firstChapterId,
    flags: {},
    clues: [],
    knowledgeCards: [],
    seenDialogues: [],
    seenTerms: [],
    chapterStats: {},
  };
}

/** 章の統計レコードを必ず存在させる */
export function ensureChapterStats(state: GameState, chapterId: string): void {
  if (!state.chapterStats[chapterId]) {
    state.chapterStats[chapterId] = { wrongHypotheses: 0, hintsUsed: 0, elapsedSec: 0 };
  }
}

/** 保存データとして妥当か（別バージョンの残骸を掴まないための最低限の検査） */
export function isValidState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const s = value as Partial<GameState>;
  return (
    typeof s.playerName === 'string' &&
    typeof s.currentChapterId === 'string' &&
    !!s.flags &&
    Array.isArray(s.clues) &&
    Array.isArray(s.knowledgeCards) &&
    Array.isArray(s.seenDialogues) &&
    Array.isArray(s.seenTerms) &&
    !!s.chapterStats
  );
}

/** localStorage が使えるか（プライベートモードや SSR 環境での例外を吸収する） */
function storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export interface SavePayload {
  state: GameState;
  /** 世界の状態も一緒に保存する（ケーブルの抜き差しは世界側の情報のため） */
  world: unknown;
  savedAt: number;
}

export function saveGame(state: GameState, world: unknown): boolean {
  const store = storage();
  if (!store) return false;
  try {
    const payload: SavePayload = { state, world, savedAt: Date.now() };
    store.setItem(SAVE_KEY, JSON.stringify(payload));
    return true;
  } catch (error) {
    console.warn('[save] 保存に失敗しました', error);
    return false;
  }
}

export function loadGame(): SavePayload | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavePayload>;
    if (!parsed || !isValidState(parsed.state)) return null;
    return { state: parsed.state, world: parsed.world, savedAt: parsed.savedAt ?? 0 };
  } catch (error) {
    console.warn('[save] 読み込みに失敗しました', error);
    return null;
  }
}

export function hasSave(): boolean {
  return loadGame() !== null;
}

export function clearSave(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(SAVE_KEY);
  } catch (error) {
    console.warn('[save] 削除に失敗しました', error);
  }
}

/** 設定の保存（音量・視野角など）。セーブデータとは分けて保持する */
export function saveJson(key: string, value: unknown): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('[save] 設定の保存に失敗しました', error);
  }
}

export function loadJson<T>(key: string): T | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
